import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useApiGet } from '../../../utils/httpClientTs';
import { getSPDChaptersList } from '../../../endpoints/chaptersTs';
import { SPDChapterListResponse } from '../../../types/chapters';
import ClientSideTable, {
  ClientSideTableColumn,
} from '../../../Components/ClientSideTable';

const columns: ClientSideTableColumn<SPDChapterListResponse>[] = [
  {
    id: 'abbreviation',
    label: 'Abbreviation',
    width: '10%',
    firstCell: true,
    sortable: true,
  },
  {
    id: 'name',
    label: 'Name',
    width: '50%',
    sortable: true,
  },
  {
    id: 'chapter_type',
    label: 'Type',
    width: '40%',
    sortable: true,
  },
];

const SPDChapterList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: allChapters, isFetching } = useApiGet<SPDChapterListResponse[]>(
    ['spd-chapters'],
    getSPDChaptersList,
    {
      enabled: true,
      retry: 1,
    }
  );

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!allChapters) return [];

    if (!search.trim()) return allChapters;

    const searchLower = search.toLowerCase();
    return allChapters.filter(
      (chapter) =>
        chapter.name.toLowerCase().includes(searchLower) ||
        chapter.abbreviation.toLowerCase().includes(searchLower) ||
        chapter.chapter_type.toLowerCase().includes(searchLower)
    );
  }, [allChapters, search]);

  const handleRowClick = (chapter: SPDChapterListResponse) => {
    navigate(`/spd-chapters/${chapter.id}`);
  };

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          Loading SPD chapters...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" color="primary" align="center" gutterBottom>
            S/P/D Chapter List
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Search"
            id="search-field"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder="Search by name, abbreviation, or type..."
          />
        </Grid>

        <Grid item xs={12}>
          <ClientSideTable
            data={filteredData}
            columns={columns}
            loading={isFetching}
            onRowClick={handleRowClick}
            emptyMessage="No SPD chapters found"
            initialRowsPerPage={25}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SPDChapterList;
